/**
 * Native API Client per Cloudflare Pages Functions
 * Sistema di chiamate API dirette senza dipendenze Firebase
 *
 * FIX APPLICATI:
 * - Rimossa cleanId() buggata - backend gestisce già :version suffix
 * - Tipi onesti: Promise<T> invece di ApiResponse<T> (backend ritorna oggetti raw)
 * - Auth headers aggiunti a imagesApi.upload
 */

// API base URL - fallback per sviluppo
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Get current session token from localStorage
 */
function getSessionToken(): string | null {
  return localStorage.getItem('session_token');
}

/**
 * Create common headers with authentication
 */
function createHeaders(contentType: boolean = true): HeadersInit {
  const headers: HeadersInit = {};
  if (contentType) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Generic API GET request
 */
export async function apiGet<T = any>(endpoint: string): Promise<T> {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      headers: createHeaders()
    });

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        errorBody = 'Unable to read error body';
      }
      throw new Error(`API Error ${response.status} on ${endpoint}: ${errorBody}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API GET failed:', error);
    throw error;
  }
}

/**
 * Generic API POST request
 */
export async function apiPost<T = any>(endpoint: string, data: any): Promise<T> {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        errorBody = 'Unable to read error body';
      }
      throw new Error(`API Error ${response.status} on ${endpoint}: ${errorBody}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API POST failed:', error);
    throw error;
  }
}

/**
 * Generic API PATCH request
 */
export async function apiPatch<T = any>(endpoint: string, data: any): Promise<T> {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        errorBody = 'Unable to read error body';
      }
      throw new Error(`API Error ${response.status} on ${endpoint}: ${errorBody}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API PATCH failed:', error);
    throw error;
  }
}

/**
 * Generic API DELETE request
 */
export async function apiDelete<T = any>(endpoint: string): Promise<T> {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: createHeaders()
    });

    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        errorBody = 'Unable to read error body';
      }
      throw new Error(`API Error ${response.status} on ${endpoint}: ${errorBody}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API DELETE failed:', error);
    throw error;
  }
}

/**
 * Authentication API
 */
export const authApi = {
  async login(email: string, password: string) {
    return apiPost('/api/auth/login', { email, password });
  },

  async register(email: string, password: string, displayName?: string) {
    return apiPost('/api/auth/register', { email, password, displayName });
  },

  async logout() {
    try {
      // Best effort: prova a chiamare API, ma pulisci comunque
      await apiPost('/api/auth/logout', {});
    } catch (error) {
      // Ignora errori API - puliamo comunque il client
      console.warn('Logout API failed (non-critical):', error);
    } finally {
      // Pulisci SEMPRE il token locale, anche se API fallisce
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
    }
    return { success: true };
  },

  async getCurrentUser() {
    return apiGet('/api/auth/me');
  },

  async resetPassword(email: string) {
    return apiPost('/api/auth/reset-password', { email });
  },

  async confirmPasswordReset(token: string, newPassword: string) {
    return apiPost('/api/auth/complete-reset', { token, newPassword: newPassword });
  }
};

/**
 * Events API
 */
export const eventsApi = {
  async list() {
    return apiGet('/api/events');
  },

  async get(id: string) {
    return apiGet(`/api/events/${id}`);
  },

  async create(data: any) {
    return apiPost('/api/events', data);
  },

  async update(id: string, data: any) {
    return apiPatch(`/api/events/${id}`, data);
  },

  async delete(id: string) {
    return apiDelete(`/api/events/${id}`);
  },

  async getStands(eventId: string) {
    return apiGet(`/api/events/${eventId}/stands`);
  },

  async getQRCode(eventId: string) {
    return apiGet(`/api/events/${eventId}/qrcode`);
  }
};

/**
 * Stands API
 */
export const standsApi = {
  async list(filters?: { eventId?: string; sponsorId?: string }) {
    const params = new URLSearchParams();
    if (filters?.eventId) params.set('eventId', filters.eventId);
    if (filters?.sponsorId) params.set('sponsorId', filters.sponsorId);

    const query = params.toString() ? `?${params}` : '';
    return apiGet(`/api/stands${query}`);
  },

  async get(id: string) {
    return apiGet(`/api/stands/${id}`);
  },

  async create(data: any) {
    return apiPost('/api/stands', data);
  },

  async update(id: string, data: any) {
    return apiPatch(`/api/stands/${id}`, data);
  },

  async delete(id: string) {
    return apiDelete(`/api/stands/${id}`);
  },

};

/**
 * Notifications API
 */
export const notificationsApi = {
  async list(userId?: string) {
    const endpoint = userId ? `/api/notifications/${userId}` : '/api/notifications';
    return apiGet(endpoint);
  },

  async create(data: any) {
    return apiPost('/api/notifications', data);
  },

  async markAsRead(id: string) {
    return apiPatch(`/api/notifications/${id}`, { read: true });
  },

  async markAllAsRead(userId: string) {
    return apiPost(`/api/notifications/batch-read`, { userId });
  },

  async delete(id: string) {
    return apiDelete(`/api/notifications/${id}`);
  }
};

/**
 * Templates API
 */
export const templatesApi = {
  async list() {
    return apiGet('/api/templates');
  },

  async get(id: string) {
    return apiGet(`/api/templates/${id}`);
  }
};

/**
 * Documents API
 */
export const documentsApi = {
  async upload(standId: string, documentId: string, file: File) {
    const formData = new FormData();
    formData.append('standId', standId);
    formData.append('documentId', documentId);
    formData.append('file', file);

    const token = getSessionToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/api/documents/upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }
};

/**
 * Images API
 */
export const imagesApi = {
  async upload(file: File, path?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (path) {
      formData.append('path', path);
    }

    const token = getSessionToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/api/upload-image`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }
};

export default {
  apiGet,
  apiPost,
  apiPatch,
  apiDelete,
  auth: authApi,
  events: eventsApi,
  stands: standsApi,
  notifications: notificationsApi,
  templates: templatesApi,
  documents: documentsApi,
  images: imagesApi
};
